/** "Templates ▾" for every reply / compose box (Juffrey / Dean, 12 Oct 2026): one shared team library across the group,
 *  tagged by mailbox (Accounts · Legal · Applications · All) and region (UK · AU · All). Insert fills the placeholders
 *  {first_name} {customer_name} {sf_number} {my_name} from the matched file / sender when known; "Save current email as
 *  template…" stores what's in the box. Delete: the author or an Admin. */
import { sanitizedHtmlProps } from "@/lib/sanitizeEmailHtml";
import { useEffect, useState } from "react";
import { ChevronDown, LayoutTemplate, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { createEmailTemplate, deleteEmailTemplate, listEmailTemplates, markEmailTemplateUsed } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

export const PLACEHOLDERS = ["{first_name}", "{customer_name}", "{sf_number}", "{my_name}"];
const DEPT_LABEL = { all: "All mailboxes", accounts: "Accounts", legal: "Legal", applications: "Applications" };
const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** First name from a sender / contact display name — skips company-looking names. */
export const firstNameOf = (name) => {
  const n = (name || "").trim();
  if (!n || /\b(ltd|limited|plc|llp|pty|trust|trading|services|group|holdings|t\/a)\b/i.test(n) || /@/.test(n)) return "";
  const first = n.split(/[\s,]+/)[0] || "";
  return first.length > 1 && !/^[A-Z0-9&.-]+$/.test(first) ? first : (first.length > 1 ? first[0] + first.slice(1).toLowerCase() : "");
};

/** Replace known placeholders (HTML-escaped); unknown ones stay visible so the writer fills them by hand. */
export const fillPlaceholders = (html, ctx = {}, { escape = true } = {}) =>
  (html || "").replace(/\{\s*(first_name|customer_name|sf_number|my_name)\s*\}/gi, (m, key) => {
    const v = ctx[key.toLowerCase()];
    return v ? (escape ? esc(v) : String(v)) : m;
  });

const isBlankHtml = (html) => !(html || "").replace(/<p>\s*<\/p>|<br\s*\/?>/g, "").replace(/<[^>]+>/g, "").trim();

export function EmailTemplatesMenu({ dept = "all", region = "all", context = {}, getCurrent, onInsert, disabled = false, testId = "email-templates" }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const deptKey = dept === "mine" ? "all" : dept;

  const load = async () => {
    try { setItems((await listEmailTemplates(dept, region)).items || []); } catch { setItems([]); }
  };
  useEffect(() => { if (open && items === null) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const insert = (t) => {
    onInsert({ body_html: fillPlaceholders(t.body_html, context), subject: fillPlaceholders(t.subject, context, { escape: false }), template: t });
    markEmailTemplateUsed(t.id);
    toast.success(`Template "${t.name}" inserted${PLACEHOLDERS.some((p) => (t.body_html || "").includes(p)) ? " — check any placeholders left in {braces}" : ""}`);
  };

  const remove = async (e, t) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Delete the template "${t.name}" for everyone?`)) return;
    try { await deleteEmailTemplate(t.id); setItems((xs) => (xs || []).filter((x) => x.id !== t.id)); toast.success(`Template "${t.name}" deleted`); }
    catch (err) { toast.error(errorDetailMessage(err, err.message)); }
  };

  const current = getCurrent ? getCurrent() : {};
  const canSave = !disabled && !isBlankHtml(current.body_html);

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" disabled={disabled} data-testid={`${testId}-btn`}>
            <LayoutTemplate className="w-3 h-3 mr-1" />Templates<ChevronDown className="w-3 h-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-80 max-w-[calc(100vw-24px)] max-h-[70vh] overflow-y-auto text-xs" data-testid={`${testId}-menu`}>
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Team templates · {DEPT_LABEL[deptKey] || deptKey}{region !== "all" ? ` · ${region === "AUS" ? "AU" : region}` : ""}
          </DropdownMenuLabel>
          {items === null && <div className="px-2 py-2 text-muted-foreground flex items-center gap-1" data-testid={`${testId}-loading`}><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>}
          {items && items.length === 0 && <div className="px-2 py-2 text-muted-foreground" data-testid={`${testId}-empty`}>No templates yet — write the email, then "Save current email as template".</div>}
          {(items || []).map((t) => (
            <DropdownMenuItem key={t.id} onSelect={() => insert(t)} className="group flex items-start gap-2 cursor-pointer" data-testid={`${testId}-item-${t.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{t.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {t.subject ? `${t.subject} · ` : ""}{DEPT_LABEL[t.dept] || t.dept}{t.region !== "all" ? ` · ${t.region === "AUS" ? "AU" : t.region}` : ""}
                  {t.created_by_name ? ` · ${t.created_by_name}` : ""}{t.uses ? ` · used ${t.uses}×` : ""}
                </div>
              </div>
              {t.can_edit && (
                <button type="button" className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 shrink-0 mt-0.5" title="Delete for everyone"
                  onClick={(e) => remove(e, t)} onPointerDown={(e) => e.stopPropagation()} data-testid={`${testId}-delete-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setSaveOpen(true)} disabled={!canSave} className="cursor-pointer" data-testid={`${testId}-save`}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />Save current email as template…
            {!canSave && <span className="ml-auto text-[10px] text-muted-foreground">write something first</span>}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {saveOpen && (
        <SaveTemplateDialog dept={deptKey} region={region} current={current} busy={busy} setBusy={setBusy} testId={testId}
          onClose={() => setSaveOpen(false)}
          onSaved={(t) => { setItems((xs) => [...(xs || []), t].sort((a, b) => a.name.localeCompare(b.name))); setSaveOpen(false); }} />
      )}
    </>
  );
}

function SaveTemplateDialog({ dept, region, current, busy, setBusy, onClose, onSaved, testId }) {
  const [name, setName] = useState("");
  const [subject, setSubject] = useState(current.subject || "");
  const [scopeDept, setScopeDept] = useState(dept === "all" ? "all" : dept);
  const [scopeRegion, setScopeRegion] = useState("all");
  const submit = async () => {
    if (!name.trim()) { toast.error("Give the template a name"); return; }
    setBusy(true);
    try {
      const t = await createEmailTemplate({ name: name.trim(), subject: subject.trim(), body_html: current.body_html, body_text: current.body_text || "", dept: scopeDept, region: scopeRegion });
      toast.success(`Template "${t.name}" saved for ${scopeDept === "all" ? "every mailbox" : DEPT_LABEL[scopeDept]}${scopeRegion === "all" ? "" : ` (${scopeRegion === "AUS" ? "AU" : "UK"} only)`}`);
      onSaved(t);
    } catch (e) { toast.error(errorDetailMessage(e, e.message)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid={`${testId}-save-dialog`}>
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><LayoutTemplate className="w-4 h-4 text-blue-600" />Save as template</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-[11px] font-medium text-slate-600">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Payment reminder — due today" autoFocus className="h-8 text-sm" data-testid={`${testId}-save-name`} />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Subject <span className="text-muted-foreground font-normal">(optional — used by New email)</span></label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line to suggest" className="h-8 text-sm" data-testid={`${testId}-save-subject`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-slate-600">Mailbox</label>
              <select value={scopeDept} onChange={(e) => setScopeDept(e.target.value)} className="h-8 w-full text-xs border rounded px-1.5 bg-white" data-testid={`${testId}-save-dept`}>
                <option value="all">All mailboxes</option>
                <option value="accounts">Accounts only</option>
                <option value="legal">Legal only</option>
                <option value="applications">Applications only</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600">Region</label>
              <select value={scopeRegion} onChange={(e) => setScopeRegion(e.target.value)} className="h-8 w-full text-xs border rounded px-1.5 bg-white" data-testid={`${testId}-save-region`}>
                <option value="all">UK and Australia</option>
                <option value="UK">UK only</option>
                <option value="AUS">Australia only</option>
              </select>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Shared with the whole team. Placeholders fill in when inserted: <code>{"{first_name}"}</code> <code>{"{customer_name}"}</code> <code>{"{sf_number}"}</code> <code>{"{my_name}"}</code>.
            Your sign-off is added on sending, so leave it out of the template.
          </p>
          <div className="border rounded p-2 bg-slate-50 max-h-40 overflow-y-auto text-xs [&_p]:my-0 [&_p:empty]:min-h-[1.2em]" data-testid={`${testId}-save-preview`} dangerouslySetInnerHTML={sanitizedHtmlProps(current.body_html)} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !name.trim()} data-testid={`${testId}-save-submit`}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Insert helper shared by the dialogs: replaces an empty box, otherwise appends under a blank line. */
export const insertTemplateHtml = (currentHtml, templateHtml) => (isBlankHtml(currentHtml) ? templateHtml : `${currentHtml}<p></p>${templateHtml}`);
